import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useBranding } from '../Context/BrandingContext';
import { useLoginModal } from '../Context/LoginModalContext';
import { getSessionToken } from '../Services/services';
import { getCart } from '../api/inventory';
import MoreDropdown from './MoreDropdown';

function CartIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0 }} aria-hidden="true">
      <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
    </svg>
  );
}

// Default brand icon shown in place of an uploaded logo. Defined once here so every
// page that falls back to the default brand (i.e. doesn't pass its own `left`) renders
// the exact same fallback — previously Landing.jsx had its own copy of this icon while
// every other page showed no icon at all when no logo was uploaded.
function IconPlane({ color, size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
      <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" />
    </svg>
  );
}

/**
 * AppHeader — shared top nav bar.
 *
 * Props:
 *   variant  'dark' (primaryColor bg, white text) | 'light' (white bg, dark text). Default 'dark'.
 *   title    string — centered page title.
 *   left     ReactNode — overrides the entire left section (defaults to the brand logo/name).
 *
 * The top-left brand (logo + company name) is always shown and always links to the
 * landing page ('/'), for consistency across every marketplace screen — there is no
 * per-page "back" navigation link in the header.
 *
 * Right section is always: RFQs · Orders · Account · Cart (N)
 * Logout is NOT in the header. It belongs on the Account page.
 */
export default function AppHeader({ variant = 'light', title, left }) {
  const { branding } = useBranding();
  const { openLogin } = useLoginModal();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = !!getSessionToken();

  const [cartCount, setCartCount] = useState(0);

  const refreshCartCount = () => {
    if (!getSessionToken()) { setCartCount(0); return; }
    getCart().then(d => setCartCount((d?.items || []).length)).catch(() => {});
  };

  useEffect(() => {
    refreshCartCount();
    window.addEventListener('cartUpdated', refreshCartCount);
    return () => window.removeEventListener('cartUpdated', refreshCartCount);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const isDark = variant === 'dark';
  const primary = branding.primaryColor;
  // `lightColor` drives the light-variant header background, `darkColor` drives its
  // text/accent color — previously these were hardcoded and the admin-configured
  // values had no effect anywhere in the UI.
  const lightBg = branding.lightColor || '#ffffff';
  const lightText = branding.darkColor || '#111827';

  const navBtn = (path) => {
    const active = location.pathname.startsWith(path);
    if (isDark) {
      return {
        textDecoration: 'none', color: 'white', cursor: 'pointer', background: 'none', border: 'none',
        display: 'inline-flex', alignItems: 'center',
        padding: '5px 10px', fontSize: 13, fontWeight: active ? 700 : 500, borderRadius: 6,
        backgroundColor: active ? 'rgba(255,255,255,0.22)' : 'transparent',
        outline: active ? '1px solid rgba(255,255,255,0.4)' : '1px solid transparent',
        transition: 'background-color 0.15s',
      };
    }
    return {
      textDecoration: 'none', color: active ? primary : lightText, cursor: 'pointer',
      background: 'none', border: 'none',
      display: 'inline-flex', alignItems: 'center',
      padding: '5px 10px', fontSize: 13, fontWeight: active ? 700 : 500, borderRadius: 6,
      backgroundColor: active ? `${primary}1a` : 'transparent',
      outline: active ? `1px solid ${primary}50` : '1px solid transparent',
      transition: 'background-color 0.15s',
    };
  };

  const defaultBrandStyle = isDark
    ? { display: 'inline-flex', alignItems: 'center', gap: 8, color: 'white', textDecoration: 'none', fontWeight: 700, fontSize: 18 }
    : { display: 'inline-flex', alignItems: 'center', gap: 8, color: lightText, textDecoration: 'none', fontWeight: 700, fontSize: 18 };

  const guestBtnStyle = isDark
    ? { background: 'none', border: 'none', padding: '5px 4px', fontSize: 13, fontWeight: 600, color: 'white', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '3px' }
    : { background: 'none', border: 'none', padding: '5px 4px', fontSize: 13, fontWeight: 600, color: '#4b5563', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '3px' };

  const headerStyle = isDark
    ? { backgroundColor: primary, padding: '0 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', color: 'white' }
    : { backgroundColor: lightBg, padding: '16px 32px', boxShadow: '0 1px 2px rgba(0,0,0,0.06)' };

  const innerStyle = {
    maxWidth: 1200, margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    height: isDark ? 64 : 'auto',
    gap: 16,
  };

  // The brand (logo + company name) is always shown on the left and always links to
  // the landing page — no back-navigation link ever replaces it. When no logo has been
  // uploaded, every page falls back to the same IconPlane placeholder (see above) so the
  // header looks identical everywhere instead of only the landing page showing an icon.
  let leftContent = left;
  if (!leftContent) {
    leftContent = (
      <Link to="/" style={defaultBrandStyle}>
        {branding.logoUrl ? (
          <img src={branding.logoUrl} alt={branding.companyName} style={{ height: isDark ? 32 : 28, width: 'auto', display: 'block' }} />
        ) : (
          <IconPlane color={isDark ? 'white' : primary} size={isDark ? 32 : 28} />
        )}
        <span>{branding.companyName}</span>
      </Link>
    );
  }

  return (
    <header style={headerStyle}>
      <div style={innerStyle}>
        {/* Left */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {leftContent}
        </div>

        {/* Center title */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', fontWeight: 700, fontSize: 18, color: isDark ? 'white' : lightText }}>
          {title || null}
        </div>

        {/* Right nav */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4 }}>
          {isAuthenticated && (
            <>
              <Link to="/my-rfqs" style={navBtn('/my-rfqs')}>Quotes</Link>
              <Link to="/my-orders" style={navBtn('/my-orders')}>Orders</Link>
              <MoreDropdown navBtn={navBtn} isDark={isDark} primary={primary} lightText={lightText} />
            </>
          )}
          {isAuthenticated ? (
            <button type="button" style={navBtn('/account')} onClick={() => navigate('/account')}>Account</button>
          ) : (
            <button type="button" style={guestBtnStyle} onClick={() => openLogin({ redirectPath: '/catalog' })}>
              Request access or sign in
            </button>
          )}
          <Link to="/cart" style={{ ...navBtn('/cart'), gap: 5 }}>
            <CartIcon />
            Cart&nbsp;({cartCount})
          </Link>
        </div>
      </div>
    </header>
  );
}
